import React from "react";
import { prettyUrl } from "./recordModel.mjs";

// The label/value pair used by every expansion on the contact detail page —
// timeline rows, the company panel and the header. Two columns, dense, and an
// expected-but-missing value always renders: the gap is the information.

export const isBlank = (v) =>
  v === null || v === undefined || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length);

/** A fact whose absence is simply absence — renders an em dash. */
export function Fact({ label, children }) {
  return (
    <div className="crm-tlfact">
      <span className="crm-tlfact__k">{label}</span>
      <span className="crm-tlfact__v">{isBlank(children) ? "—" : children}</span>
    </div>
  );
}

/**
 * A firmographic field: an empty one renders as an em dash plus a muted
 * "enrich" marker and is NEVER hidden — a visible gap is what creates the
 * pressure to fill it.
 *
 * The marker has two forms, and the difference is whether anything can act on
 * it. Given `onEnrich` it is a real button on POST /contacts/:id/enrich; without
 * one it stays the statement it has always been — a gap that Apollo will fill
 * when the lead is qualified (src/enrich/onQualified.js), not a control. A
 * marker that looks clickable where no handler exists is a promise the API
 * cannot keep, so the two forms are never allowed to look alike.
 */
export function Firmo({ label, value, href, onEnrich, enriching }) {
  if (isBlank(value)) {
    return (
      <div className="crm-tlfact">
        <span className="crm-tlfact__k">{label}</span>
        <span className="crm-tlfact__v">
          —
          {onEnrich ? (
            <button
              type="button"
              className="crm-enrich crm-enrich--btn"
              disabled={enriching}
              onClick={onEnrich}
              title="Look this up in Apollo now — fills empty fields only, never overwrites"
            >
              {enriching ? "enriching…" : "enrich"}
            </button>
          ) : (
            <span
              className="crm-enrich"
              title="Empty — Apollo fills this when the lead is qualified"
            >
              enrich
            </span>
          )}
        </span>
      </div>
    );
  }
  return (
    <div className="crm-tlfact">
      <span className="crm-tlfact__k">{label}</span>
      <span className="crm-tlfact__v">
        {href
          ? <a className="crm-row2__link" href={href} target="_blank" rel="noreferrer">{prettyUrl(value)}</a>
          : value}
      </span>
    </div>
  );
}

/** A bare string → an absolute URL, so "acme.com" still opens. */
export function httpUrl(v) {
  if (isBlank(v)) return null;
  const s = String(v).trim();
  return /^https?:\/\//i.test(s) ? s : `https://${s.replace(/^\/+/, "")}`;
}

// The readable form of a URL, decoded (recordModel.mjs, where it is tested).
export { prettyUrl };
